
import { IncomeAndExpenseCalculations } from 'app/calcs/Accountant/Transactions'
import * as TX from 'app/calcs/Accountant/Transactions/interfaces'
import { calculateRetirementScore, IScoreTransactionsOutput, scoreTransactions } from 'app/calcs/Planners/Fidelity'
import { IRetirementPerson, Profile, retirementDate } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as DateCalcs from 'app/utils/DateCalcs'
import * as _ from 'lodash'
import * as moment from 'moment'

interface IClassifiedTransactionData {
  income: number
  expenses: number
  balance: number
}

interface IClumpData extends IClassifiedTransactionData {
  clumpName: string
  earliestDate: moment.Moment
  latestDate: moment.Moment
}

function analyzeTransactions(transactions: TX.ITransactionItem[]): TX.ITransactionsAnalysis {
  if (0 === transactions.length) { return { grossIncome: 0, averageMonthlyNetIncome: 0, averageMonthlyExpenses: 0, score: 0, scores: [], transactions: [] } }

  const items = _.orderBy(transactions, [(x) => x.when.unix()])
  const bins = binByIncomeAndExpenses(() => 'totals')(items)

  // compute number of months (by looking at first and last)
  const months = moment.duration(_.last(items).when.diff(_.first(items).when)).asMonths() + 1
  const averageMonthlyExpenses = bins.expenses / months
  const averageMonthlyNetIncome = (bins.income + bins.expenses) / months
  const scores = scoreTransactions(items)
  const r = { grossIncome: bins.income, totalExpenses: bins.expenses, averageMonthlyNetIncome, averageMonthlyExpenses, score: _.meanBy(scores, (q) => q.score), scores, transactions: items }

  // tslint:disable-next-line:no-console
  //console.log('analyzeTransactions- results: ' + JSON.stringify(r))

  return r
}

/**
 * Takes a list of transactions and sorts them into 2 bins (income & expenses) and a running balance.
 *
 * @param namer A function that will name a transaction
 */
const binByIncomeAndExpenses = (namer: TX.ITransactionItemNamer) =>
  (clump: TX.ITransactionItem[]): IClumpData =>
    _.reduce(clump, (r: IClumpData, tx) => {
      // tslint:disable-next-line:no-console
      //console.log('tx when: ' + tx.when.format('L'))
      // tslint:disable-next-line:no-debugger
      //debugger
      if (! r.earliestDate || tx.when.isBefore(r.earliestDate)) {
        r.earliestDate = tx.when
        r.clumpName = namer(tx) // make sure the series is named with the earliest date
      }
      if (! r.latestDate || tx.when.isAfter(r.latestDate)) { r.latestDate = tx.when }
      classifyTransaction(tx, r)
      return r
    }, { clumpName: 'no data', income: 0, expenses: 0, balance: 0, earliestDate: undefined, latestDate: undefined })

function calculateGrandScore(profile: Profile, startWhen: moment.Moment, endWhen: moment.Moment): Promise<TX.ITransactionsAnalysis> {
  const incomeAndExpenseCalcs = new IncomeAndExpenseCalculations(profile)
  const getTransactions = incomeAndExpenseCalcs.getTransactions.bind(incomeAndExpenseCalcs)

  startWhen = DateCalcs.toCalcDate(startWhen)
  endWhen = DateCalcs.toCalcDate(endWhen)

  return Promise.all(DateCalcs.generateMonths(startWhen, endWhen).map(getTransactions))
    .then(_.flatten)
    .then(analyzeTransactions)
}

/**
 * Splits all the elements in the 'what' hash into income and expense.
 *
 * @param tx - Transaction to classify
 * @param acc - optional accumulator
 */
function classifyTransaction(tx: TX.ITransactionItem, acc?: IClassifiedTransactionData): IClassifiedTransactionData {
  if (!acc) { acc = { income: 0, expenses: 0, balance: 0 } }
  _.keysIn(tx.what).forEach((k) => {
    let v = tx.what[k]
    // sometimes we get called with timeline transactions that have 'expenses' as a positive value
    if (k === 'expenses') { v = -v }
    if (v >= 0) {
      acc.income += v
      // tslint:disable-next-line:no-console
      //console.log('income: ' + v + ', total: ' + acc.income + ', source: ' + JSON.stringify(tx))
    } else {
      acc.expenses -= v
      // tslint:disable-next-line:no-console
      //console.log('expense: ' + v + ', total: ' + acc.expenses + ', source: ' + JSON.stringify(tx))
    }
    acc.balance += v
  })
  return acc
}

const groupByFractionalYears = (tx: TX.ITransactionItem): number => utils.fractionalYear(tx.when)

function makeGroupByMultipleYears(mod: number) {
  return (tx: TX.ITransactionItem): number => utils.round(tx.when.year() / mod)
}

const nameByMonthAndYear = (tx: TX.ITransactionItem): string => tx.when.format('MMM YYYY')

const nameByYear = (tx: TX.ITransactionItem): string => tx.when.format('YYYY')

const nameByTwoYears = (tx: TX.ITransactionItem): string => tx.when.format('YYYY') + '-' + (tx.when.year() % 100 + 1)

function printTransactions(transactions: TX.ITransactionItem[]) {
  transactions.forEach((tx) => {
    const a = _(tx.what).valuesIn().sum()
    // tslint:disable-next-line:no-console
    console.info(tx.when.format('MM-YYYY') + '\t' + tx.description + '\t' + a)
  })
}

/**
 * Sum all the properties of a single transaction.
 *
 * @param tx - the transaction whose properties need to be summed
 */
const sumTransactions = (tx: TX.ITransactionItem) => _(tx.what).valuesIn().flatten().sum()

/**
 * Sum all the properties of a single transaction and produce the negated result. Useful in descending sorts.
 *
 * @param tx - the transaction whose properties need to be summed
 */
const sumTransactionsNegated = (transaction: TX.ITransactionItem) => -sumTransactions(transaction)

/**
 * Sorts a list of transactions ascending by date and descending by the sum of the contained items.
 * Useful in producing an ongoing bank balance where deposits hit the balance before expenses.
 *
 * @param items the list of transactions to sort
 */
const sortByWhenAndAmountDesc = (items: TX.ITransactionItem[]) => _.orderBy(items, [utils.byWhen, sumTransactionsNegated])

export {
  analyzeTransactions,
  binByIncomeAndExpenses,
  calculateGrandScore,
  classifyTransaction,
  groupByFractionalYears,
  makeGroupByMultipleYears,
  nameByMonthAndYear,
  nameByYear,
  nameByTwoYears,
  printTransactions,
  sortByWhenAndAmountDesc,
  sumTransactions,
  sumTransactionsNegated,

  IClassifiedTransactionData
}



// WEBPACK FOOTER //
// ./src/app/calcs/Accountant/Transactions/predicates.ts